import { t } from 'i18next'
import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { StorageKeys } from '~/atoms/StorageKeys'
import { FilterOption } from '~/components/core/FilterMenu'
import { type Lid, useLid } from '~/hooks/useLid'
import { useGetExtensions } from '~/queries/extensionQueries'
import {
  type FaxSearch,
  UnreadExtensionCounts,
  useDeleteFax,
  useFaxList,
  useGetFax,
  useToggleUnread,
  useUnreadCount,
  useUnreadCountByExtension,
} from '~/queries/faxQueries'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace, FaxAnalyticsEvents } from '~/types/Analytics'
import { FaxDirectionFilter } from '~/types/ApiRequestTypes'
import { SearchValue } from '~/types/Shared'
import { phoneFormatter } from '~/utils/formatter'
import { showErrorToast, showSuccessToast } from '~/utils/toast'

import { useDebouncedCallback } from '@mangovoice/core'
import type {
  ApiError,
  ExtensionInstance,
  Fax,
  FaxInstance,
  ResourceLoadingDisposition,
} from '@mangovoice/types'

export type DirectionFilterMenu = {
  lid: Lid
  options: DirectionFilterOption[]
}

export type DirectionFilterOption = FilterOption<FaxDirectionFilter>

export interface FaxStateAtom {
  directionFilter: FaxDirectionFilter
  extensionFilter: FilterExtension
  faxes: Fax[]
  faxPdfPageNumber?: number
  unreadCount: { byExtension: UnreadExtensionCounts; total: number }
  searchValue?: SearchValue
  selectedFaxId?: number
  selectedFaxInstanceLoadingState?: ResourceLoadingDisposition
  statusFilter: FilterStatus
}

export type FaxStateReact = {
  directionFilterMenu: DirectionFilterMenu
  extensionIds: number[]
  extensions: ExtensionInstance[]
  faxesLoading?: boolean
  faxListLoadingState?: ResourceLoadingDisposition
  faxSearch: FaxSearch
  selectedFaxInstance?: FaxInstance
}

/** React hooks can't be used by Atom, so separate them accordingly */
export type FaxViewModelState = FaxStateAtom & FaxStateReact

export interface FaxLocalStorageState {
  directionFilter: FaxDirectionFilter
  extensionFilter: FilterExtension
  statusFilter: FilterStatus
}

/** number is an extension ID */
export type FilterExtension = 'all' | number

export type FilterStatus = 'all' | 'unread'

export type FilterStatusOption = FilterOption<FilterStatus>

export const filterDefaults = {
  direction: FaxDirectionFilter.ALL as FaxDirectionFilter,
  extension: 'all' as FilterExtension,
  status: 'unread' as FilterStatus,
} as const

export const cacheDefault: FaxLocalStorageState = {
  directionFilter: filterDefaults.direction,
  extensionFilter: filterDefaults.extension,
  statusFilter: filterDefaults.status,
}

const directionFilterMenuOptions: () => DirectionFilterOption[] = () => [
  {
    label: t('fax.filter.all'),
    value: FaxDirectionFilter.ALL,
    default: filterDefaults.direction === FaxDirectionFilter.ALL,
  },
  {
    label: t('fax.filter.sent'),
    value: FaxDirectionFilter.OUTBOUND,
    default: filterDefaults.direction === FaxDirectionFilter.OUTBOUND,
  },
  {
    label: t('fax.filter.received'),
    value: FaxDirectionFilter.INBOUND,
    default: filterDefaults.direction === FaxDirectionFilter.INBOUND,
  },
]

export const statusFilterOptions: () => FilterStatusOption[] = () => [
  {
    label: t('fax.filter.unread'),
    value: 'unread',
    default: filterDefaults.status === 'unread',
  },
  {
    label: t('fax.filter.all'),
    value: 'all',
    default: filterDefaults.status === 'all',
  },
]

let rootState: FaxStateAtom = {
  directionFilter: filterDefaults.direction,
  extensionFilter: filterDefaults.extension,
  faxes: [],
  unreadCount: { byExtension: new Map(), total: 0 },
  statusFilter: filterDefaults.status,
}

const initialStateAtom = atom<FaxStateAtom>(rootState)

const faxLocalStorageAtom = atomWithStorage<FaxLocalStorageState>(
  StorageKeys.FaxLs,
  cacheDefault,
)

/**
 * The debounce delay (in milliseconds) for the phone number search input.
 *
 * - Set to 300 ms to align with the needs of receptionists in dental offices,
 *   who form the primary user demographic of this application.
 * - Phone number inputs are typically short, and users tend to complete their
 *   typing quickly, making a shorter debounce delay suitable for fast lookups.
 * - Receptionists often multitask, so the 300 ms delay ensures quick feedback
 *   without overwhelming the system with excessive API calls.
 * - Balances responsiveness and efficiency, optimizing the experience for
 *   receptionists who rely on accurate and immediate search results.
 */
const searchDebounceMs = 300

const useFaxRoutePathParams = () => {
  const { faxId } = useParams<{ faxId?: string }>()

  useEffect(() => {
    if (!faxId) {
      return
    }
    // The viewmodel is mounted by several components at once, each running this
    // effect; the analytics layer drops the identical repeats.
    observe.track(AnalyticsNamespace.FAXING, FaxAnalyticsEvents.Selected, {
      id: faxId,
    })
  }, [faxId])

  return faxId ? Number(faxId) : null
}

const getNumber = ((fax: FaxInstance | undefined): string | undefined => {
  if (!fax) {
    return undefined as undefined
  } else {
    if (fax.direction === 'inbound') {
      return phoneFormatter(fax.from_phone_number.phone_number) as string
    } else {
      return phoneFormatter(fax.to_phone_number.phone_number) as string
    }
  }
}) as ((_: FaxInstance) => string) &
  ((_: FaxInstance | undefined) => string | undefined)

export const useFaxViewModel = () => {
  const [__state, __setState] = useAtom(initialStateAtom)
  const [__ls, __setLs] = useAtom(faxLocalStorageAtom)
  const [searchDebounced, setSearchDebounced] = useState<SearchValue | null>(
    __state.searchValue || null,
  )
  const changeSearchDebounced = useDebouncedCallback((value: SearchValue) => {
    setSearchDebounced(value)
  }, searchDebounceMs)
  const faxID = useFaxRoutePathParams()
  const unreadCount = useUnreadCount()
  const directionFilterMenuLid = useLid()
  const { results: extensions, fetchNextPage: getMoreExtensions } =
    useGetExtensions({
      type: 'VirtualFaxMachine',
    })
  const extensionIds = useMemo(
    () => extensions.map((extension) => extension.id),
    [extensions],
  )
  const unreadCountByExtension = useUnreadCountByExtension(extensionIds)

  const directionFilterMenu = useMemo<DirectionFilterMenu>(
    () => ({
      lid: directionFilterMenuLid,
      options: directionFilterMenuOptions(),
    }),
    [directionFilterMenuLid],
  )

  const setState = (newState: FaxStateAtom) => {
    const updated = { ...newState }
    rootState = updated
    __setState(updated)
    __setLs({
      directionFilter: updated.directionFilter,
      extensionFilter: updated.extensionFilter,
      statusFilter: updated.statusFilter,
    })
  }

  const appendState = (newState: Partial<FaxStateAtom>) => {
    const updated = { ...rootState, ...newState }
    setState(updated)
  }

  const setExtensionFilter = (next: FilterExtension) => {
    appendState({
      extensionFilter: next,
    })
  }

  const setStatusFilter = (next: FilterStatus) => {
    appendState({
      statusFilter: next,
    })
  }

  const setDirectionFilter = (direction?: FaxDirectionFilter) => {
    appendState({
      directionFilter: direction,
    })
  }

  /**
   * Updates the fax list search input immediately
   * and triggers a debounced server call to filter the list
   *
   * @see `faxSearch` for the search query that will be sent to the server
   */
  const setSearchValue = (_value: SearchValue) => {
    appendState({
      searchValue: _value,
    })
    changeSearchDebounced(_value)
  }

  /** @param options.hard - When present, will reset filters to default values. Filters persist in local storage, so a hard reset will override those values */
  function resetState(options?: { hard?: boolean }) {
    setState({
      directionFilter: __state.directionFilter,
      extensionFilter: __state.extensionFilter,
      faxes: [],
      unreadCount: { byExtension: new Map(), total: 0 },
      statusFilter: __state.statusFilter,
      ...(options?.hard && cacheDefault),
    })
  }

  const successCallback = () => {
    showSuccessToast({ message: t('faxPage.toast.deleteSuccess') })
  }

  const errorCallback = (error: ApiError) => {
    showErrorToast({
      message: error.message || t('faxPage.toast.unexpectedError'),
    })
  }

  const faxSearch = useMemo<FaxSearch>(
    () => ({
      search: searchDebounced?.value,
      direction: __state.directionFilter,
      extension:
        __state.extensionFilter === 'all' ? undefined : __state.extensionFilter,
      unread:
        // `true` matches left, `false` matches right, and `undefined` matches the union.
        __state.statusFilter === 'all'
          ? undefined
          : __state.statusFilter === 'unread',
    }),
    [
      searchDebounced,
      __state.directionFilter,
      __state.extensionFilter,
      __state.statusFilter,
    ],
  )

  const {
    faxes,
    isLoading: faxesLoading,
    get,
    remove,
    fetchNextPage: tryGetNextFaxPage,
  } = useFaxList(faxSearch)

  const deleteMutation = useDeleteFax({
    onSuccess: successCallback,
    onError: errorCallback,
  })

  const deleteFax = (faxId: number) => {
    remove(faxId)
    deleteMutation.mutate(faxId)
  }

  const { data: fax } = useGetFax(faxID, {
    initialData: get(faxID) as FaxInstance,
  })

  const toggleUnread = useToggleUnread(faxID, faxSearch, extensionIds)

  const state: FaxViewModelState = {
    ...__state,
    directionFilterMenu,
    extensionIds,
    extensions,
    faxes,
    faxesLoading,
    faxSearch,
    selectedFaxInstance: fax,
    selectedFaxId: fax?.id,
    unreadCount: {
      byExtension: unreadCountByExtension.results,
      total: unreadCount.results,
    },
  }

  return {
    state,
    actions: {
      tryGetNextFaxPage,
      deleteFax,
      getMoreExtensions,
      resetState,
      setExtensionFilter,
      setStatusFilter,
      setDirectionFilter,
      setSearchValue,
      toggleUnread,
    },
    utils: {
      getNumber,
    },
  }
}

export function isCache(candidate: any): candidate is FaxLocalStorageState {
  return (
    candidate &&
    typeof candidate === 'object' &&
    'directionFilter' in candidate &&
    'statusFilter' in candidate
  )
}
